import React, { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Form, FieldArray, FormikProvider, ErrorMessage } from 'formik';
import {
  Box,
  Grid,
  Radio,
  Button,
  Divider,
  FormLabel,
  TextField,
  Typography,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Chip,
  MenuItem,
  Checkbox
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import { callRestGetEndpoint } from '@/common/utils/api';

const useStyles: any = makeStyles(() => ({
  errorMessage: {
    color: 'red',
    marginTop: '5px',
    display: 'block'
  },
  itemPriceNumbering: {
    width: '40px',
    height: '40px',
    fontWeight: 'bold',
    lineHeight: '40px',
    textAlign: 'center',
    marginRight: '20px',
    borderRadius: '100%',
    display: 'inline-block',
    border: '1px solid #333333'
  },
  headerText: {
    fontSize: '18px',
    fontWeight: 'bold'
  },
  pricingTierIds: {
    fontSize: '15px'
  }
}));

export const IV_DRIP_CATEGORY_ID = 211;

function AddEditProduct(props) {
  const classes = useStyles();
  const { formik, pricingTiers, newItem, returnToList } = props;
  const { values, setValues } = formik;
  const { getFieldProps, handleChange, handleSubmit, isSubmitting } = formik;
  const [creditTypes, setCreditTypes] = useState([]);
  const [purchaseCategories, setPurchaseCategories] = useState([]);
  const [dripIngredients, setDripIngredients] = useState([]);
  const [showErrors, setShowErrors] = useState(false);

  const addGrantInForm = newField => {
    const newForm = Object.assign({}, { ...values });
    newForm[newField] = [...newForm[newField]];
    newForm[newField].push({
      credit_type: '',
      num_to_grant: 0,
      expires_days: 0
    });
    setValues(newForm);
  };

  const resetIngredientForm = () => {
    const newForm = Object.assign({}, { ...values });
    newForm.drip_ingredients = [];
    setValues(newForm);
  };

  const handleIngredientReset = () => {
    if (values.is_cocktail) {
      resetIngredientForm();
    }
  };

  const addIngredientInForm = newField => {
    const newForm = Object.assign({}, { ...values });
    newForm[newField] = [...newForm[newField]];
    newForm[newField].push({
      ingredient: '',
      quantity: 1
    });
    setValues(newForm);
  };

  const removeGrantInForm = index => {
    const creditGrantsCopy = [...values.credit_grants];
    creditGrantsCopy.splice(index, 1);
    const newForm = {
      ...values,
      credit_grants: creditGrantsCopy
    };
    setValues(newForm);
  };

  const removeIngredientInForm = index => {
    const dripIngredientsCopy = [...values.drip_ingredients];
    dripIngredientsCopy.splice(index, 1);
    const newForm = {
      ...values,
      drip_ingredients: dripIngredientsCopy
    };
    setValues(newForm);
  };

  const clearItemPriceCredits = () => {
    setValues(prevValues => ({
      ...prevValues,
      item_price_credits: []
    }));
  };

  const getCreditTypes = async () => {
    try {
      const res = await callRestGetEndpoint(
        { getAll: true },
        '/pos_cart/get_credit_types/'
      );
      const creditTypes = res?.creditTypes;
      setCreditTypes(creditTypes);
    } catch (e) {
      console.error(e);
    }
  };

  const getPurchaseCategories = async () => {
    try {
      const res = await callRestGetEndpoint(
        { return_all: true },
        '/pos_product/get_product_categories/'
      );
      const purchaseCategories = res?.data?.categories;
      setPurchaseCategories(purchaseCategories);
    } catch (e) {
      console.error(e);
    }
  };

  const getDripIngredients = async () => {
    try {
      const res = await callRestGetEndpoint(
        {},
        '/pos_product/get_drip_ingredients/'
      );
      const dripIngredients = res?.dripIngredients;
      setDripIngredients(dripIngredients);
    } catch (e) {
      console.error(e);
    }
  };

  const renderErrors = (error, key) => {
    if (typeof error === 'string') {
      return (
        <Typography key={key} variant="body1" color="error">
          {error}
        </Typography>
      );
    } else if (Array.isArray(error)) {
      return error.map((err, index) => renderErrors(err, `${key}-${index}`));
    } else if (typeof error === 'object') {
      return Object.keys(error).map(subKey =>
        renderErrors(error[subKey], `${key}-${subKey}`)
      );
    }
    return null;
  };

  useEffect(() => {
    getCreditTypes();
    getPurchaseCategories();
  }, []);

  useEffect(() => {
    if (
      (values?.purchase_category === IV_DRIP_CATEGORY_ID ||
        values?.purchase_category?.id === IV_DRIP_CATEGORY_ID) &&
      !dripIngredients.length
    ) {
      getDripIngredients();
    }
  }, [values?.purchase_category]);

  return (
    <FormikProvider value={formik}>
      <Form noValidate onSubmit={handleSubmit}>
        <Grid container spacing={5}>
          <Grid item xs={12} md={9}>
            <Grid container spacing={3}>
              {!values?.type && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      className={classes.headerText}
                    >
                      Select Item Type
                    </Typography>
                    <Divider />
                  </Grid>
                  <Grid item xs={6} md={12}>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        aria-label="itemType"
                        name="item_type"
                        {...getFieldProps('type')}
                      >
                        <FormControlLabel
                          label="Product"
                          value="product"
                          control={<Radio />}
                        />
                        <FormControlLabel
                          label="Item Pack"
                          value="bundle"
                          control={<Radio />}
                        />
                        <FormControlLabel
                          label="Membership"
                          value="membership"
                          control={<Radio />}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </>
              )}
              {values?.type && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      className={classes.headerText}
                    >
                      Purchasable Item ID
                    </Typography>
                    <Divider />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="text"
                      name="id"
                      label="ID"
                      placeholder="Cannot be changed once item is created!"
                      InputLabelProps={{
                        shrink: true
                      }}
                      value={values.id}
                      disabled={!newItem}
                      onChange={handleChange}
                    />
                    <Typography className={classes.errorMessage}>
                      <ErrorMessage name="id" />
                    </Typography>
                  </Grid>
                </>
              )}
              {values?.type && (values?.type != 'membership' || !newItem) && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      className={classes.headerText}
                    >
                      Purchase Category
                    </Typography>
                    <Divider />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      name="purchase_category"
                      label="Select Purchase Category"
                      InputLabelProps={{
                        shrink: true
                      }}
                      disabled={!newItem}
                      {...getFieldProps('purchase_category')}
                    >
                      {purchaseCategories.map(option => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.id} - {option.description}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Typography className={classes.errorMessage}>
                      <ErrorMessage name="purchase_category" />
                    </Typography>
                  </Grid>
                </>
              )}
              {values?.type && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      className={classes.headerText}
                    >
                      Purchasable Item Information
                    </Typography>
                    <Divider />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField
                      fullWidth
                      type="text"
                      name="description"
                      label="Description"
                      InputLabelProps={{
                        shrink: true
                      }}
                      value={values.description}
                      onChange={handleChange}
                    />
                    <Typography className={classes.errorMessage}>
                      <ErrorMessage name="description" />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      type="text"
                      name="short_description"
                      label="Short Description"
                      InputLabelProps={{
                        shrink: true
                      }}
                      value={values.short_description}
                      onChange={handleChange}
                    />
                    <Typography className={classes.errorMessage}>
                      <ErrorMessage name="short_description" />
                    </Typography>
                  </Grid>
                  {values?.type == 'product' && (
                    <>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="text"
                          name="duration"
                          label="Duration"
                          InputLabelProps={{
                            shrink: true
                          }}
                          value={values.duration}
                          onChange={handleChange}
                        />
                        <Typography className={classes.errorMessage}>
                          <ErrorMessage name="duration" />
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="text"
                          name="padding_before_virgin"
                          label="Padding Before Virgin"
                          InputLabelProps={{
                            shrink: true
                          }}
                          value={values.padding_before_virgin}
                          onChange={handleChange}
                        />
                        <Typography className={classes.errorMessage}>
                          <ErrorMessage name="padding_before_virgin" />
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="text"
                          name="padding_before"
                          label="Padding Before"
                          InputLabelProps={{
                            shrink: true
                          }}
                          {...getFieldProps('padding_before')}
                        />
                        <Typography className={classes.errorMessage}>
                          <ErrorMessage name="padding_before" />
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="text"
                          name="paddingAfter"
                          label="Padding After"
                          InputLabelProps={{
                            shrink: true
                          }}
                          {...getFieldProps('padding_after')}
                        />
                        <Typography className={classes.errorMessage}>
                          <ErrorMessage name="padding_after" />
                        </Typography>
                      </Grid>
                    </>
                  )}
                  {values?.type == 'membership' && (
                    <>
                      <Grid item xs={12} md={12} style={{ marginTop: '-10px' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                formik.values.membership_type.is_universal ||
                                false
                              }
                              onChange={event => {
                                formik.setFieldValue(
                                  'membership_type.is_universal',
                                  event.target.checked
                                );
                              }}
                            />
                          }
                          label="Universal Membership?"
                        />
                      </Grid>
                      <Grid item xs={12} md={12} style={{ marginTop: '-20px' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formik.values.callback === 'promo'}
                              onChange={event => {
                                formik.setFieldValue(
                                  'callback',
                                  event.target.checked ? 'promo' : null
                                );
                              }}
                            />
                          }
                          label="Promo Membership?"
                        />
                      </Grid>
                      {values?.callback == 'promo' && (
                        <Grid item xs={12} md={12}>
                          <TextField
                            fullWidth
                            name="excludedRollsInto"
                            label="Memberships Excluded from Rolls Into (Comma Separated)"
                            placeholder="110000000, 110000001, 110000002"
                            InputLabelProps={{
                              shrink: true
                            }}
                            {...getFieldProps('excluded_rolls_into')}
                          />
                          <Typography className={classes.errorMessage}>
                            <ErrorMessage name="excluded_rolls_into" />
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </>
              )}
              {values?.type == 'membership' && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      className={classes.headerText}
                    >
                      Membership Information
                    </Typography>
                    <Divider />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="text"
                      name="expires_months"
                      label="Months to Expire"
                      InputLabelProps={{
                        shrink: true
                      }}
                      {...getFieldProps('membership_type.expires_months')}
                    />
                    <Typography className={classes.errorMessage}>
                      <ErrorMessage name="expiresMonths" />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="text"
                      name="credits_on_pay"
                      label="Credits on Pay"
                      InputLabelProps={{
                        shrink: true
                      }}
                      {...getFieldProps('membership_type.credits_on_pay')}
                    />
                    <Typography className={classes.errorMessage}>
                      <ErrorMessage name="credits_on_pay" />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="text"
                      name="credits_daily"
                      label="Credits Daily"
                      InputLabelProps={{
                        shrink: true
                      }}
                      {...getFieldProps('membership_type.credits_daily')}
                    />
                    <Typography className={classes.errorMessage}>
                      <ErrorMessage name="credits_daily" />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="text"
                      name="founder_discount"
                      label="Founder Discount"
                      InputLabelProps={{
                        shrink: true
                      }}
                      {...getFieldProps('membership_type.founder_discount')}
                    />
                    <Typography className={classes.errorMessage}>
                      <ErrorMessage name="founder_discount" />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="text"
                      name="billing_frequency"
                      label="Billing Frequency"
                      InputLabelProps={{
                        shrink: true
                      }}
                      {...getFieldProps('membership_type.billing_frequency')}
                    />
                    <Typography className={classes.errorMessage}>
                      <ErrorMessage name="billing_frequency" />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="text"
                      name="billing_interval"
                      label="Billing Interval"
                      InputLabelProps={{
                        shrink: true
                      }}
                      {...getFieldProps('membership_type.billing_interval')}
                    />
                    <Typography className={classes.errorMessage}>
                      <ErrorMessage name="billing_interval" />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="text"
                      name="billing_cycles"
                      label="Billing Cycles"
                      InputLabelProps={{
                        shrink: true
                      }}
                      {...getFieldProps('membership_type.billing_cycles')}
                    />
                    <Typography className={classes.errorMessage}>
                      <ErrorMessage name="billing_cycles" />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}></Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="text"
                      name="display_order"
                      label="Cart Display Order"
                      InputLabelProps={{
                        shrink: true
                      }}
                      {...getFieldProps('membership_type.display_order')}
                    />
                    <Typography className={classes.errorMessage}>
                      <ErrorMessage name="display_order" />
                    </Typography>
                  </Grid>
                  {values?.membership_type?.display_order !== null && (
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        type="text"
                        name="display_group"
                        label="Cart Display Group"
                        InputLabelProps={{
                          shrink: true
                        }}
                        {...getFieldProps('membership_type.display_group')}
                      />
                      <Typography className={classes.errorMessage}>
                        <ErrorMessage name="display_group" />
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                </>
              )}
              {values?.type == 'bundle' && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      className={classes.headerText}
                    >
                      Item Pack Credit Information
                    </Typography>
                    <Divider />
                  </Grid>
                </>
              )}
              {(values?.type == 'bundle' || values?.type == 'membership') && (
                <>
                  <Grid item xs={12}>
                    <FieldArray
                      name="creditGrants"
                      render={() => (
                        <Grid container spacing={3}>
                          {values.credit_grants?.map((item, itemIndex) => (
                            <>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  select
                                  name={`credit_grants[${itemIndex}].credit_type`}
                                  label="Select Credit Type"
                                  InputLabelProps={{
                                    shrink: true
                                  }}
                                  {...getFieldProps(
                                    `credit_grants[${itemIndex}].credit_type`
                                  )}
                                >
                                  {creditTypes.map(option => (
                                    <MenuItem key={option.id} value={option.id}>
                                      {option.description}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <Typography className={classes.errorMessage}>
                                  <ErrorMessage
                                    name={`credit_grants[${itemIndex}].credit_type`}
                                  />
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  type="text"
                                  name={`credit_grants[${itemIndex}].num_to_grant`}
                                  label="Credits to Grant"
                                  InputLabelProps={{
                                    shrink: true
                                  }}
                                  {...getFieldProps(
                                    `credit_grants[${itemIndex}].num_to_grant`
                                  )}
                                />
                                <Typography className={classes.errorMessage}>
                                  <ErrorMessage
                                    name={`credit_grants[${itemIndex}].num_to_grant`}
                                  />
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  type="text"
                                  name={`credit_grants[${itemIndex}].expires_days`}
                                  label="Days to Expire"
                                  InputLabelProps={{
                                    shrink: true
                                  }}
                                  {...getFieldProps(
                                    `credit_grants[${itemIndex}].expires_days`
                                  )}
                                />
                                <Typography className={classes.errorMessage}>
                                  <ErrorMessage
                                    name={`credit_grants[${itemIndex}].expires_days`}
                                  />
                                </Typography>
                              </Grid>
                              {(values?.type != 'bundle' ||
                                values.credit_grants.length > 1) && (
                                <>
                                  <Grid
                                    item
                                    xs={12}
                                    display="flex"
                                    justifyContent="flex-end"
                                  >
                                    <Button
                                      type="button"
                                      variant="outlined"
                                      onClick={() =>
                                        removeGrantInForm(itemIndex)
                                      }
                                    >
                                      <DeleteIcon />
                                    </Button>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Divider />
                                  </Grid>
                                </>
                              )}
                            </>
                          ))}
                        </Grid>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => addGrantInForm('credit_grants')}
                    >
                      {values?.credit_grants?.length === 0
                        ? 'Add Credit Grant'
                        : 'Add More Credit Grants'}
                    </Button>
                  </Grid>
                </>
              )}
              {(values?.purchase_category === IV_DRIP_CATEGORY_ID ||
                values?.purchase_category?.id === IV_DRIP_CATEGORY_ID) &&
                values?.type == 'product' && (
                  <>
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        className={classes.headerText}
                      >
                        Drip Nutrients
                      </Typography>
                      <Divider />
                    </Grid>
                    {newItem && (
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...getFieldProps('is_cocktail')}
                              checked={values.is_cocktail}
                              onClick={handleIngredientReset}
                            />
                          }
                          label="Is product a drip cocktail?"
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <FieldArray
                        name="dripIngredients"
                        render={() => (
                          <Grid container spacing={3}>
                            {values.drip_ingredients?.map((item, itemIndex) => (
                              <>
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    select
                                    name={`drip_ingredients[${itemIndex}].ingredient`}
                                    label="Select Drip Nutrient"
                                    InputLabelProps={{
                                      shrink: true
                                    }}
                                    {...getFieldProps(
                                      `drip_ingredients[${itemIndex}].ingredient`
                                    )}
                                    disabled={!values.is_cocktail}
                                  >
                                    {dripIngredients.map(option => (
                                      <MenuItem
                                        key={option.id}
                                        value={option.id}
                                      >
                                        {option.description}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  <Typography className={classes.errorMessage}>
                                    <ErrorMessage
                                      name={`drip_ingredients[${itemIndex}].ingredient`}
                                    />
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    fullWidth
                                    type="number"
                                    name={`drip_ingredients[${itemIndex}].quantity`}
                                    label="Quantity"
                                    InputLabelProps={{
                                      shrink: true
                                    }}
                                    {...getFieldProps(
                                      `drip_ingredients[${itemIndex}].quantity`
                                    )}
                                    disabled={!values.is_cocktail}
                                  />
                                  <Typography className={classes.errorMessage}>
                                    <ErrorMessage
                                      name={`drip_ingredients[${itemIndex}].quantity`}
                                    />
                                  </Typography>
                                </Grid>
                                <Grid
                                  item
                                  xs={12}
                                  md={3}
                                  display="flex"
                                  justifyContent="flex-end"
                                >
                                  <Button
                                    type="button"
                                    variant="outlined"
                                    onClick={() =>
                                      removeIngredientInForm(itemIndex)
                                    }
                                    disabled={!values.is_cocktail}
                                  >
                                    <DeleteIcon />
                                  </Button>
                                </Grid>
                                <Grid item xs={12}>
                                  <Divider />
                                </Grid>
                              </>
                            ))}
                          </Grid>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => addIngredientInForm('drip_ingredients')}
                        disabled={!values.is_cocktail}
                      >
                        {values?.drip_ingredients?.length === 0
                          ? 'Add Drip Nutrient'
                          : 'Add More Drip Nurtients'}
                      </Button>
                    </Grid>
                  </>
                )}
              {values?.type && (
                <>
                  {/* Item Prices Start */}
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      className={classes.headerText}
                    >
                      Item Prices by Tier
                    </Typography>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox {...getFieldProps('same_price_all')} />
                      }
                      label="Same price for all tiers?"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FieldArray
                      name="itemPrices"
                      render={() => (
                        <Grid container spacing={3}>
                          {values.same_price_all ? (
                            <>
                              {values?.type != 'membership' && (
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    type="text"
                                    label="Member Price"
                                    {...getFieldProps(
                                      'item_price.0.member_price'
                                    )}
                                  />
                                  <Typography>
                                    <ErrorMessage name="item_price.0.member_price" />
                                  </Typography>
                                </Grid>
                              )}
                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  type="text"
                                  label="Non-Member Price"
                                  {...getFieldProps(
                                    'item_price.0.non_member_price'
                                  )}
                                />
                                <Typography>
                                  <ErrorMessage name="item_price.0.non_member_price" />
                                </Typography>
                              </Grid>
                              {values?.type != 'membership' && (
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    type="text"
                                    label="Credit Cost"
                                    {...getFieldProps(
                                      'item_price.0.credit_cost'
                                    )}
                                  />
                                  <Typography>
                                    <ErrorMessage name="item_price.0.credit_cost" />
                                  </Typography>
                                </Grid>
                              )}
                            </>
                          ) : (
                            pricingTiers?.map((tier, index) => (
                              <>
                                <Grid item xs={12} key={index}>
                                  <Typography
                                    className={classes.itemPriceNumbering}
                                  >
                                    {tier['description']}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    className={classes.pricingTierIds}
                                  >
                                    Legacy tier {tier['id']}
                                  </Typography>
                                </Grid>
                                {values?.type != 'membership' && (
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      fullWidth
                                      type="text"
                                      label="Member Price"
                                      name={`item_price.${
                                        tier['id'] - 1
                                      }.member_price`}
                                      {...getFieldProps(
                                        `item_price.${
                                          tier['id'] - 1
                                        }.member_price`
                                      )}
                                    />
                                    <Typography
                                      className={classes.errorMessage}
                                    >
                                      <ErrorMessage
                                        name={`item_price.${
                                          tier['id'] - 1
                                        }.member_price`}
                                      />
                                    </Typography>
                                  </Grid>
                                )}
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    type="text"
                                    label="Non-Member Price"
                                    name={`item_price.${
                                      tier['id'] - 1
                                    }.non_member_price`}
                                    {...getFieldProps(
                                      `item_price.${
                                        tier['id'] - 1
                                      }.non_member_price`
                                    )}
                                  />
                                  <Typography className={classes.errorMessage}>
                                    <ErrorMessage
                                      name={`item_price.${
                                        tier['id'] - 1
                                      }.non_member_price`}
                                    />
                                  </Typography>
                                </Grid>
                                {values?.type != 'membership' && (
                                  <Grid item xs={12} md={2}>
                                    <TextField
                                      fullWidth
                                      type="text"
                                      label="Credit Cost"
                                      name={`item_price.${
                                        tier['id'] - 1
                                      }.credit_cost`}
                                      {...getFieldProps(
                                        `item_price.${
                                          tier['id'] - 1
                                        }.credit_cost`
                                      )}
                                    />
                                    <Typography
                                      className={classes.errorMessage}
                                    >
                                      <ErrorMessage
                                        name={`item_price.${
                                          tier['id'] - 1
                                        }.credit_cost`}
                                      />
                                    </Typography>
                                  </Grid>
                                )}
                              </>
                            ))
                          )}
                        </Grid>
                      )}
                    />
                  </Grid>
                  {values?.type == 'product' && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          select
                          name="item_price_credits"
                          label="Select Credits Item Can Use (In Priority Order)"
                          SelectProps={{
                            multiple: true,
                            renderValue: selected => (
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  overflowX: 'hidden',
                                  maxWidth: '100%',
                                  alignItems: 'flex-start'
                                }}
                              >
                                {(selected as string[]).map((value, index) => (
                                  <Chip
                                    key={value}
                                    label={
                                      `[${index + 1}] ` +
                                      creditTypes.find(
                                        option => option.id === value
                                      )?.description
                                    }
                                    style={{
                                      margin: '2px 0',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      maxWidth: '90%'
                                    }}
                                  />
                                ))}
                              </div>
                            )
                          }}
                          InputLabelProps={{
                            shrink: true
                          }}
                          {...getFieldProps('item_price_credits')}
                        >
                          {creditTypes.map(option => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.description}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Typography className={classes.errorMessage}>
                          <ErrorMessage name="itemPriceCredits" />
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        display="flex"
                        justifyContent="flex-start"
                      >
                        <Button
                          type="button"
                          variant="outlined"
                          onClick={() => clearItemPriceCredits()}
                        >
                          Clear Credits
                        </Button>
                      </Grid>
                    </>
                  )}
                  {/* Item Prices End */}
                </>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} md={3}>
            <Grid container spacing={3}>
              {values?.type && (
                <>
                  <Grid item xs={6} md={12}>
                    <FormControl component="fieldset">
                      <FormLabel>Active Status</FormLabel>
                      <RadioGroup
                        row
                        aria-label="is_active"
                        name="is_active"
                        {...getFieldProps('is_active')}
                      >
                        <FormControlLabel
                          label="Active"
                          value="true"
                          control={<Radio />}
                        />
                        <FormControlLabel
                          label="Inactive"
                          value="false"
                          control={<Radio />}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} md={8}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6} md={12}>
                    <FormControl component="fieldset">
                      <FormLabel>Is Item Medical?</FormLabel>
                      <RadioGroup
                        row
                        aria-label="is_medical"
                        name="is_medical"
                        {...getFieldProps('is_medical')}
                      >
                        <FormControlLabel
                          label="Yes"
                          value="true"
                          control={<Radio />}
                        />
                        <FormControlLabel
                          label="No"
                          value="false"
                          control={<Radio />}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} md={12}>
                    <FormControl component="fieldset">
                      <FormLabel>Is Item Taxable?</FormLabel>
                      <RadioGroup
                        row
                        name="taxable"
                        {...getFieldProps('taxable')}
                      >
                        <FormControlLabel
                          label="Yes"
                          value="true"
                          control={<Radio />}
                        />
                        <FormControlLabel
                          label="No"
                          value="false"
                          control={<Radio />}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  {values?.type == 'membership' && (
                    <Grid item xs={6} md={12}>
                      <FormControl component="fieldset">
                        <FormLabel>Is Membership Editable?</FormLabel>
                        <RadioGroup
                          row
                          name="is_editable"
                          {...getFieldProps('membership_type.is_editable')}
                        >
                          <FormControlLabel
                            label="Yes"
                            value="true"
                            control={<Radio />}
                          />
                          <FormControlLabel
                            label="No"
                            value="false"
                            control={<Radio />}
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} md={12}>
            <Box display="flex" justifyContent="flex-end">
              {Object.keys(formik.errors).length > 0 && (
                <Box mr={1}>
                  <Button
                    size="large"
                    variant="outlined"
                    color="error"
                    onClick={() => setShowErrors(!showErrors)}
                  >
                    Show Errors
                  </Button>
                </Box>
              )}
              <Box mr={1}>
                <Button size="large" variant="outlined" onClick={returnToList}>
                  Cancel
                </Button>
              </Box>
              <LoadingButton
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                disabled={values?.item_price?.length === 0}
              >
                {newItem
                  ? `Create Item${values.id ? ` (${values.id})` : ''}`
                  : 'Save'}
              </LoadingButton>
            </Box>
          </Grid>
          {Object.keys(formik.errors).length > 0 && showErrors && (
            <Grid item xs={12}>
              <Box display="flex" flexDirection="column" alignItems="flex-end">
                {Object.keys(formik.errors).map(key =>
                  renderErrors(formik.errors[key], key)
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Form>
    </FormikProvider>
  );
}
export default AddEditProduct;
